import { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

const SUPPORTED_LANGUAGES = ['fr', 'en', 'de', 'nl'];

const FALLBACK_LANGUAGE = 'fr';

/**
 * Détecte la langue du navigateur et retourne une langue supportée, ou 'fr' en fallback.
 * Utilise navigator.languages puis navigator.language.
 */
function getBrowserPreferredLanguage() {
  if (typeof navigator === 'undefined') return FALLBACK_LANGUAGE;
  const sources = [
    ...(Array.isArray(navigator.languages) ? navigator.languages : []),
    navigator.language,
    navigator.userLanguage, // IE
  ].filter(Boolean);
  for (const locale of sources) {
    const code = (locale.split('-')[0] || '').toLowerCase();
    if (SUPPORTED_LANGUAGES.includes(code)) return code;
  }
  return FALLBACK_LANGUAGE;
}

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(() => {
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage && SUPPORTED_LANGUAGES.includes(savedLanguage)) {
      return savedLanguage;
    }
    return getBrowserPreferredLanguage();
  });

  useEffect(() => {
    // Sauvegarder la langue dans localStorage
    localStorage.setItem('language', language);
  }, [language]);

  const changeLanguage = (lang) => {
    // Valider que la langue est supportée avant de la définir
    if (SUPPORTED_LANGUAGES.includes(lang)) {
      setLanguage(lang);
    }
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

